import classNames from 'classnames';
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import AppPaths from '../../AppPaths';
import { ReactComponent as CloseIcon } from '../../assets/images/close-navigation.svg';
import FiMiniImage from '../../assets/images/nav-fi-mini.png';
import FiSeries3PlusImage from '../../assets/images/nav-fi-series-three-plus.png';
import FiMiniCatsImage from '../../assets/images/nav-fi-mini-cats.png';
import FiUltraImage from '../../assets/images/nav-fi-ultra.png';
import { getDomainConfig } from '../../lib/domainConfig';
import { miniCollarId, series3PlusCollarId, ultraCollarId } from '../../types';
import styles from './AppBar.module.scss';
import LoginLogoutButton from './LoginLogoutButton';
import { collarProductIsSupportedInCountry, useLocale } from '../../contexts/LocaleContext';
import CountrySelector from './CountrySelector';
import { ReactComponent as Logo } from '../../assets/images/fi-logo.svg';
import { ReactComponent as ArrowRightIcon } from '../../assets/images/arrow_right.svg';
import { useTranslation } from 'react-i18next';
import { SaleBadge } from '../SaleBadge/SaleBadge';
import { getFiGrowthBook, useIsGrowthBookEnabled } from '../../lib/growthbook';
import { navFreeTrialClicked } from '../../lib/analytics/events';

interface IMainNavigationV2Props {
  dismissMobileMenu(): void;
}

function MainNavigationV2({ dismissMobileMenu }: IMainNavigationV2Props) {
  const { country } = useLocale();
  const { pathname } = useLocation();
  const renderCountrySelector = !pathname.includes('/checkout');
  const { t } = useTranslation();
  const { baseDomain } = getDomainConfig();

  const isUltraExperienceEnabled = useIsGrowthBookEnabled()
    ? getFiGrowthBook().getFeatureValue<boolean>('ultra-experience', false)
    : false;
  const isFreeTrialEnabled = useIsGrowthBookEnabled()
    ? getFiGrowthBook().getFeatureValue<boolean>('free-trial-enabled', false)
    : false;

  return (
    <div className={classNames(styles.nav, styles.navV2)}>
      <div className={styles.headerContainer}>
        <div className={styles.headerLeft}>
          <a href={`https://${baseDomain}`}>
            <Logo style={{ fill: 'black' }} />
          </a>
          {renderCountrySelector && (
            <div className={classNames(styles.linkGroup)}>
              <CountrySelector />
            </div>
          )}
        </div>
        <div className={styles.headerRight}>
          <CloseIcon style={{ fill: 'black' }} onClick={dismissMobileMenu} className={styles.toggle} />
        </div>
      </div>
      <div className={styles.container}>
        <div className={classNames(styles.linkGroup, styles.productLinks)}>
          {collarProductIsSupportedInCountry(miniCollarId, country) && (
            <Link
              to={AppPaths.Products('fi-mini') + '?type=dog'}
              className={styles.productLink}
              onClick={dismissMobileMenu}
            >
              <div className={styles.productCard}>
                <div className={styles.productImage}>
                  <SaleBadge className={styles.saleBadge} />
                  <img src={FiMiniImage} alt={t('fi_mini')} />
                  <div className={styles.productName}>{t('mini')}</div>
                </div>
              </div>
            </Link>
          )}
          <Link to={AppPaths.Products(series3PlusCollarId)} className={styles.productLink} onClick={dismissMobileMenu}>
            <div className={styles.productCard}>
              <div className={styles.productImage}>
                <SaleBadge className={styles.saleBadge} />
                <img src={FiSeries3PlusImage} alt={t('fi_series_3_plus')} />
                <div className={styles.productName}>{t('series_3_plus')}</div>
              </div>
            </div>
          </Link>
          {isUltraExperienceEnabled && (
            <Link to={AppPaths.Products(ultraCollarId)} className={styles.productLink} onClick={dismissMobileMenu}>
              <div className={styles.productCard}>
                <div className={styles.productImage}>
                  <SaleBadge className={styles.saleBadge} />
                  <img src={FiUltraImage} alt={t('fi_ultra')} />
                  <div className={styles.productName}>{t('fi_ultra')}</div>
                </div>
              </div>
            </Link>
          )}
          {collarProductIsSupportedInCountry(miniCollarId, country) && (
            <Link
              to={AppPaths.Products('fi-mini') + '?type=cat'}
              className={styles.productLink}
              onClick={dismissMobileMenu}
            >
              <div className={styles.productCard}>
                <div className={styles.productImage}>
                  <SaleBadge className={styles.saleBadge} />
                  <img src={FiMiniCatsImage} alt={t('fi_mini_cats')} />
                  <div className={styles.productName}>
                    {t('mini')}
                    <span className={styles.subProductName}>{t('for_cats')}</span>
                  </div>
                </div>
              </div>
            </Link>
          )}
          {collarProductIsSupportedInCountry(miniCollarId, country) && (
            <Link to={AppPaths.ProductListingPage} className={styles.productLink} onClick={dismissMobileMenu}>
              <div className={styles.plpCard}>
                {t('shop_all_products')} <ArrowRightIcon />
              </div>
            </Link>
          )}
        </div>
        <div className={classNames(styles.linkGroup, styles.accountLinks)}>
          <Link className={styles.link} key={'link-accessories'} to={AppPaths.Accessories} onClick={dismissMobileMenu}>
            {t('accessories')}
          </Link>
          <a className={styles.link} key={'link-the-app'} href={`https://${baseDomain}/theapp`}>
            {t('the_app')}
          </a>
          {country === 'US' && (
            <Link className={styles.link} key={'link-gift-cards'} to={AppPaths.Gift} onClick={dismissMobileMenu}>
              {t('gift_cards')}
            </Link>
          )}
          {isFreeTrialEnabled && (
            <Link
              to={AppPaths.Products(series3PlusCollarId)}
              className={styles.link}
              onClick={() => {
                navFreeTrialClicked();
                dismissMobileMenu();
              }}
            >
              {t('try_fi_for_free')}
            </Link>
          )}
          <LoginLogoutButton onClick={dismissMobileMenu} />
        </div>
      </div>
    </div>
  );
}

export default MainNavigationV2;
